import { Draggable } from "@hello-pangea/dnd";
import { useRedirect, RecordContextProvider } from "ra-core";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { differenceInDays } from "date-fns";

import { Avatar } from "./Avatar";
import type { Contact } from "../types";

export const ContactCard = ({
  contact,
  index,
}: {
  contact: Contact;
  index: number;
}) => {
  if (!contact) return null;

  return (
    <Draggable draggableId={String(contact.id)} index={index}>
      {(provided, snapshot) => (
        <ContactCardContent
          provided={provided}
          snapshot={snapshot}
          contact={contact}
        />
      )}
    </Draggable>
  );
};

export const ContactCardContent = ({
  provided,
  snapshot,
  contact,
}: {
  provided?: any;
  snapshot?: any;
  contact: Contact;
}) => {
  const redirect = useRedirect();
  const handleClick = () => {
    redirect(`/contacts/${contact.id}/show`, undefined, undefined, undefined, {
      _scrollToTop: false,
    });
  };

  const daysSinceInteraction = contact.last_meaningful_interaction_at
    ? differenceInDays(
        new Date(),
        new Date(contact.last_meaningful_interaction_at),
      )
    : 0;

  const isStale = daysSinceInteraction > 60;
  const isAging = daysSinceInteraction > 30 && daysSinceInteraction <= 60;

  const getAgingClasses = () => {
    if (isStale)
      return "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700/50";
    if (isAging)
      return "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/40";
    return "";
  };

  return (
    <div
      className="cursor-pointer"
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
      ref={provided?.innerRef}
      onClick={handleClick}
    >
      <RecordContextProvider value={contact}>
        <Card
          className={`py-3 transition-all duration-200 ${
            snapshot?.isDragging
              ? "opacity-90 transform rotate-1 shadow-lg"
              : "shadow-sm hover:shadow-md"
          } ${getAgingClasses()}`}
        >
          <CardContent className="px-3 flex flex-col gap-2">
            <div className="flex-1 flex gap-3 items-center">
              <Avatar />
              <div className="flex-1 flex flex-col">
                <p className="text-sm font-medium">
                  {contact.first_name} {contact.last_name}
                </p>
                {(contact.title || contact.company_id) && (
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {contact.title}{" "}
                    {contact.title && contact.company_id ? "at" : ""}{" "}
                    {contact.company_id && (
                      <ReferenceField
                        source="company_id"
                        reference="companies"
                        link={false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    )}
                  </p>
                )}
              </div>
            </div>
            {contact.next_relationship_move && (
              <div className="text-[10px] text-primary bg-primary/10 w-fit px-1.5 py-0.5 rounded-sm font-semibold">
                Move: {contact.next_relationship_move}
              </div>
            )}
            {contact.next_touch_date && (
              <div className="text-[10px] text-muted-foreground bg-muted w-fit px-1.5 py-0.5 rounded-sm">
                Next touch:{" "}
                {new Date(contact.next_touch_date).toLocaleDateString()}
              </div>
            )}
            {contact.last_meaningful_interaction_at && isStale && (
              <div className="text-[10px] text-orange-700 dark:text-orange-400 font-medium">
                Stale: {daysSinceInteraction} days ago
              </div>
            )}
          </CardContent>
        </Card>
      </RecordContextProvider>
    </div>
  );
};
